import {
  ALL_LAYOUT_ARGS,
  Arg,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { CARD_SIZES } from '../../../utils/spds/constants/sizes';
import { hash } from '@ember/helper';
import Body from './card/body.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Header from './card/header.gjs';
import Super from '../core/base.gjs';
import styles from './card.module.scss';

const METADATA = Metadata.define({
  description: `
Flexible containers used to display chunks of related information like statistical data or product descriptions.

### Accessibility

The card header uses a semantic \`header\` element for proper document structure. When using the \`@title\` argument, the title is automatically wrapped in an \`h2\` element for proper heading hierarchy. When using the \`:title\` named block, appropriate semantic markup (such as heading elements) must be provided.

### Contextual components

The card yields two contextual components: \`Header\` and \`Body\`

* \`Header\` - Contains the card's header content with support for title, subtitle, and action elements
* \`Body\` - Contains the main content area of the card

Either contextual component may be omitted as needed.

### Spds::Container::Card::Header

The header of the card has three named blocks: \`:action\`, \`:subtitle\`, and \`:title\`

* \`:action\` can be used to add an action button or link to the header
* \`:subtitle\` can be used to add a subtitle to the header. Alternatively, the \`@subtitle\` argument can be used to set the subtitle
* \`:title\` can be used to add a title to the header. Alternatively, the \`@title\` argument can be used to set the title

### Spds::Container::Card::Body

The body of the card contains the main content area of the card.
  `,
  extends: [
    pick(ALL_LAYOUT_ARGS, [{ name: 'gap', default: '16px' }]),
    pick(CONTAINER_ARGS, [
      'fitContent',
      'fitContentHeight',
      'fitContentWidth',
      { name: 'column', default: true },
    ]),
    pick(CONTENT_ARGS, [
      { name: 'subtitle', initialValue: 'Subtitle' },
      { name: 'title', initialValue: 'Title' },
    ]),
  ],
  meta: {
    size: Arg.string({
      description: 'Size variant of the card',
      initialValue: 'large',
      options: CARD_SIZES,
    }),
  },
  name: 'Card',
});

export default class SpdsContainerCard extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-container-card';
  classesToMapToArgs = ['size'];
  styles = styles;

  get bodyId() {
    return this.composeSubcomponentId('body');
  }

  get column() {
    return this.argOrDefault('column');
  }

  get component() {
    return this.column ? FlexColumn : FlexRow;
  }

  get gap() {
    return this.argOrDefault('gap');
  }

  get headerId() {
    return this.composeSubcomponentId('header');
  }

  <template>
    <this.component
      @fitContent={{@fitContent}}
      @fitContentHeight={{@fitContentHeight}}
      @fitContentWidth={{@fitContentWidth}}
      @gap={{this.gap}}
      class={{this.classes}}
      data-test-spds-container-card
      id={{this.id}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Body=(component Body id=this.bodyId)
          Header=(component Header id=this.headerId subtitle=@subtitle title=@title)
        )
      }}
    </this.component>
  </template>
}

export const CARD_METADATA = SpdsContainerCard.metadata;
export { CARD_SIZES as sizes };
